import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { City } from 'app/@types/shared/world-cities';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { combineLatest, startWith, Subject, take, takeUntil } from 'rxjs';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { ConfirmationService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { OrganizationAction } from '@state/organization.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UtilsService } from '@services/common/utils.service';
import { CurrencyDto } from 'app/@types/dto/currency-dto.model';
import { ToastService } from '@services/common/toast.service';
import { KpiDenominatorActions, KpiDenominatorState } from '@state/kpi-denominator.state';
import { EntityKpiDenominatorDto } from 'app/@types/dto/entity-kpi-denominator-dto.model';
import { OrganizationKpiDenominatorTypeDto } from 'app/@types/dto/organization-kpi-denominator-type-dto.model';
import { KpiDenominatorCellValue } from '../../../kpi-denominator-matrix/kpi-denominator-matrix.component';
import { PostHogService } from '@services/common/post-hog.service';
import { buildCountryOptions, CountryOption } from 'app/main/helpers/country-options';
import { destructiveConfirmation } from 'app/main/helpers/confirmation';
import { revealFormErrors } from 'app/main/helpers/form-validation';

interface Industry extends IndustryDto {
    label?: string;
}

export interface CurrencyOption {
    code: string;
    label: string;
}

interface EditEntityFormControls {
    name: FormControl<string | null>;
    country: FormControl<string | null>;
    city: FormControl<number | null>;
    industry: FormControl<string | null>;
    fte: FormControl<number | null>;
    growth: FormControl<number | null>;
    revenues: FormControl<number | null>;
    currency: FormControl<string | null>;
    comment: FormControl<string | null>;
}

@Component({
    selector: 'app-edit-entity',
    templateUrl: './edit-entity.component.html',
    styleUrls: ['./edit-entity.component.scss'],
    providers: [ConfirmationService],
    standalone: false,
})
export class EditEntityComponent implements OnInit, OnDestroy {
    @Input() public organizationId!: string;
    @Input() public entity?: OrganizationEntityDto;
    @Input() public cities!: Map<string, City[]>;
    @Input() public industries!: Industry[];
    @Input() public activeQuestionnaire!: QuestionnaireDto;
    @Input() public entities!: OrganizationEntityDto[];
    @Input() public currencies!: CurrencyDto[];
    /** Years with carbon data, ascending — columns of the KPI denominator matrix. */
    @Input() public availableYears: number[] = [];
    @Output() closeEditEntity = new EventEmitter<
        { translateKey: string; status: boolean; hasFteDecreased?: boolean } | undefined
    >();
    public entityForm: FormGroup<EditEntityFormControls>;
    public filteredCities: City[] | undefined;
    public countries: CountryOption[] = [];
    public undeletedEntities: OrganizationEntityDto[] = [];
    public currencyOptions: CurrencyOption[] = [];

    public kpiTypes: OrganizationKpiDenominatorTypeDto[] = [];
    public entityKpiDenominators: EntityKpiDenominatorDto[] = [];
    private kpiPendingCells: KpiDenominatorCellValue[] = [];
    private kpiCellsDirty = false;
    private destroy$ = new Subject<void>();

    constructor(
        private formBuilder: FormBuilder,
        private translateService: CustomTranslateService,
        private ts: TranslateService,
        private confirmationService: ConfirmationService,
        private store: Store,
        private readonly utilsService: UtilsService,
        private toastService: ToastService,
        private postHogService: PostHogService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.entityForm = new FormGroup<EditEntityFormControls>({
            name: new FormControl<string | null>('', Validators.required),
            country: new FormControl<string | null>('', Validators.required),
            city: new FormControl<number | null>(null),
            industry: new FormControl<string | null>(''),
            fte: new FormControl<number | null>(1, [Validators.required, Validators.min(1)]),
            growth: new FormControl<number | null>(0, Validators.min(0)),
            revenues: new FormControl<number | null>(null, Validators.min(0)),
            currency: new FormControl<string | null>('EUR', Validators.required),
            comment: new FormControl<string | null>(''),
        });
    }

    ngOnInit() {
        this.translateService.onLangChange.pipe(startWith(null)).subscribe(() => {
            this.translateService.get('countries').subscribe((countries) => {
                this.countries = buildCountryOptions(countries, this.translateService.currentLang);
            });

            this.industries.forEach((industry) => {
                industry.label = this.translateService.instant(
                    `components.settingsForm.industry.${industry.industrySectorLabel}`,
                );
            });
            this.industries.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

            this.currencyOptions = this.currencies.map((currency) => {
                return {
                    code: currency.code,
                    label: this.ts.instant(`common.currency.${currency.code}`) as string,
                };
            });
        });

        if (this.entity) {
            this.entityForm.controls.name.setValue(this.entity.name);
            this.entityForm.controls.fte.setValue(this.entity.employeesCount);
            this.entityForm.controls.country.setValue(this.entity.country);
            this.filteredCities = this.cities.get(this.entity.country);
            this.entityForm.controls.city.setValue(this.entity.cityCode);
            this.entityForm.controls.industry.setValue(this.entity.industryId ?? null);
            this.entityForm.controls.revenues.setValue(this.entity.annualRevenue ?? null);
            this.entityForm.controls.currency.setValue(this.entity.currency);
            this.entityForm.controls.comment.setValue(this.entity.comment ?? '');
        } else {
            this.entityForm.controls.city.disable();
        }
        this.undeletedEntities = this.entities.filter((e) => !e.deleted);

        // KPI denominators of the edited entity (loaded into the state by the parent).
        this.store
            .select(KpiDenominatorState.getTypes)
            .pipe(takeUntil(this.destroy$))
            .subscribe((types) => {
                this.kpiTypes = types;
            });
        this.store
            .select(KpiDenominatorState.getDenominators)
            .pipe(takeUntil(this.destroy$))
            .subscribe((denominators) => {
                this.entityKpiDenominators = denominators.filter(
                    (denominator) => denominator.organizationEntityId === this.entity?.id,
                );
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onKpiCellsChange(event: { cells: KpiDenominatorCellValue[]; dirty: boolean }): void {
        this.kpiPendingCells = event.cells;
        this.kpiCellsDirty = event.dirty;
    }

    filterCities() {
        this.entityForm.controls.city.setValue(null);
        const country = this.entityForm.controls.country.value;
        if (country) {
            this.filteredCities = this.cities.get(country);
            this.entityForm.controls.city.enable();
        }
    }

    public getIndustryDisplay(industryId: string): string {
        const res = this.industries.find((x) => x.industrySectorId === industryId);

        return res
            ? this.translateService.instant(`components.settingsForm.industry.${res.industrySectorLabel}`)
            : 'N/A';
    }

    save(): void {
        if (!revealFormErrors(this.entityForm, this.host.nativeElement)) {
            return;
        }
        const controls = this.entityForm.controls;
        const entity: OrganizationEntityDto = {
            organizationId: this.organizationId,
            name: controls.name.value ?? '',
            industryId: controls.industry.value ?? undefined,
            country: controls.country.value ?? '',
            cityCode: controls.city.value ?? 0,
            annualRevenue: controls.revenues.value ?? undefined,
            employeesCount: controls.fte.value ?? 0,
            isActive: true, //To remove??
            deleted: false,
            currency: controls.currency.value ?? '',
            comment: controls.comment.value ? controls.comment.value : undefined,
        };
        if (!this.entity) {
            this.createEntity(entity);
        } else {
            this.saveKpiValues();
            this.updateEntity(entity);
        }
    }

    /** Saves edited KPI denominators alongside the entity; no-op when nothing changed. */
    private saveKpiValues(): void {
        const entityId = this.entity?.id;
        if (!entityId || !this.kpiCellsDirty) {
            return;
        }
        const completion: Subject<boolean> = new Subject();
        // take(1), not takeUntil(destroy$): updateEntity() closes this dialog on success, so the
        // KPI upsert can resolve after this component is destroyed — we still want its failure toast.
        completion.pipe(take(1)).subscribe((success) => {
            if (success) {
                this.kpiCellsDirty = false;
            } else {
                this.toastService.showError({ details: 'components.settingsForm.entitySection.kpi.saveError' });
            }
        });
        this.store.dispatch(
            new KpiDenominatorActions.UpsertDenominators(
                this.organizationId,
                this.kpiPendingCells.map((cell) => ({ ...cell, organizationEntityId: entityId })),
                completion,
            ),
        );
    }

    createEntity(entity: OrganizationEntityDto) {
        if (this.entityForm.valid) {
            const callCompletion: Subject<OrganizationEntityDto | undefined> = new Subject();
            this.store.dispatch(
                new OrganizationAction.CreateEntitiesAction(this.organizationId, entity, callCompletion),
            );
            callCompletion.subscribe({
                next: () => {
                    this.postHogService.capture('entity_saved', { is_new: true });
                    this.closeEditEntity.emit({
                        translateKey: 'components.settingsForm.entitySection.toasts.creationSuccess',
                        status: true,
                    });
                },
                error: () => {
                    this.closeEditEntity.emit({
                        translateKey: 'components.settingsForm.entitySection.toasts.creationFail',
                        status: false,
                    });
                },
            });
        }
    }

    updateEntity(entity: OrganizationEntityDto) {
        if (this.entityForm.valid) {
            const callCompletion: Subject<OrganizationEntityDto | undefined> = new Subject();
            if (this.entity?.id) {
                entity.id = this.entity.id;
                this.store.dispatch(
                    new OrganizationAction.UpdateEntitieAction(
                        this.organizationId,
                        this.entity?.id,
                        entity,
                        callCompletion,
                    ),
                );
                let hasFteDecreased: boolean = false;
                if (this.entity?.employeesCount && entity.employeesCount) {
                    hasFteDecreased = this.entity.employeesCount > entity.employeesCount;
                }
                callCompletion.subscribe({
                    next: () => {
                        this.postHogService.capture('entity_saved', { is_new: false });
                        this.closeEditEntity.emit({
                            translateKey: 'components.settingsForm.entitySection.toasts.updateSuccess',
                            hasFteDecreased: hasFteDecreased,
                            status: true,
                        });
                    },
                    error: () => {
                        this.closeEditEntity.emit({
                            translateKey: 'components.settingsForm.entitySection.toasts.updateFail',
                            status: false,
                        });
                    },
                });
            }
        }
    }

    close() {
        this.closeEditEntity.emit();
    }

    onDelete(): void {
        combineLatest([
            this.translateService.get('common.confirm_message'),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm(
                destructiveConfirmation({
                    message: this.utilsService.handleTranslation(translations[0]),
                    header: this.utilsService.handleTranslation(translations[1]),
                    acceptLabel: this.utilsService.handleTranslation(translations[2]),
                    rejectLabel: this.utilsService.handleTranslation(translations[3]),
                    icon: 'ms ms-warning',
                    accept: () => {
                        if (this.entity?.id) {
                            const entityId = this.entity.id;
                            const callCompletion = new Subject<QuestionnaireDto[] | undefined>();
                            this.store.dispatch(
                                new OrganizationAction.DeleteEntityAction(
                                    this.organizationId,
                                    entityId,
                                    this.activeQuestionnaire,
                                    callCompletion,
                                ),
                            );
                            callCompletion.subscribe({
                                next: () => {
                                    this.closeEditEntity.emit({
                                        translateKey: 'components.settingsForm.entitySection.toasts.deleteSuccess',
                                        status: true,
                                    });
                                },
                                error: () => {
                                    this.closeEditEntity.emit({
                                        translateKey: 'components.settingsForm.entitySection.toasts.deleteFail',
                                        status: true,
                                    });
                                },
                            });
                        }
                    },
                }),
            );
        });
    }

    formatCommentDate(dateString: string | undefined): string {
        if (!dateString) {
            return '';
        }
        const date = new Date(dateString);
        return date.toLocaleDateString(this.translateService.currentLang, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }
}
